import { Draft, produce } from 'immer';
import clamp from 'lodash/clamp';

import {
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS,
  CONTRACT_SETUP_ALL_STATE_CLEAR,
  CONTRACT_SETUP_BY_DRAFT_REQUEST,
  CONTRACT_SETUP_BY_DRAFT_SUCCESS,
  CONTRACT_SETUP_FIELD_CREATE,
  CONTRACT_SETUP_FIELD_POSITION_CHANGE,
  CONTRACT_SETUP_FIELD_REMOVE,
  CONTRACT_SETUP_FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE,
  CONTRACT_SETUP_PARTICIPANT_REMOVE,
} from '@app/store/actionTypes';
import { ContractSetupAction } from '@app/store/contract-setup/actions';
import FieldType from '@app/store/FieldType';

export interface ContractSetupFieldsPositionsState {
  [id: string]: {
    page: number;
    relativeX: number;
    relativeY: number;
  };
}

const initialState: ContractSetupFieldsPositionsState = {};

const reducer = produce(
  (draft: Draft<ContractSetupFieldsPositionsState>, action: ContractSetupAction) => {
    switch (action.type) {
      case CONTRACT_SETUP_ALL_STATE_CLEAR:
      case CONTRACT_SETUP_BY_DRAFT_REQUEST:
      case CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST:
        return initialState;
      case CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS: {
        const { participantFields } = action.payload;
        participantFields.forEach((participantField) => {
          if (participantField.fieldType === FieldType.CHECKBOX_GROUP) {
            draft[participantField.fieldId] = {
              page: participantField.guide.locationPage,
              relativeX: 0,
              relativeY: 0,
            };
            participantField.children.forEach((child) => {
              draft[child.fieldId] = {
                page: child.guide.locationPage,
                relativeX: child.guide.locationX,
                relativeY: child.guide.locationY,
              };
            });
          } else {
            const { fieldId, guide } = participantField;
            const { locationPage, locationX, locationY } = guide;
            draft[fieldId] = {
              page: locationPage,
              relativeX: locationX,
              relativeY: locationY,
            };
          }
        });
        return draft;
      }
      case CONTRACT_SETUP_FIELD_CREATE: {
        const { page, relativeX, relativeY, uuid } = action.payload.meta;
        draft[uuid] = {
          page,
          relativeX,
          relativeY,
        };
        return draft;
      }
      case CONTRACT_SETUP_FIELD_POSITION_CHANGE: {
        const { relativeX, relativeY, uuid } = action.payload;
        draft[uuid].relativeX = clamp(relativeX, 0, 1);
        draft[uuid].relativeY = clamp(relativeY, 0, 1);
        return draft;
      }
      case CONTRACT_SETUP_FIELD_REMOVE: {
        const { uuid } = action.payload;
        delete draft[uuid];
        return draft;
      }
      case CONTRACT_SETUP_PARTICIPANT_REMOVE: {
        const { participantFieldIds } = action.payload;
        participantFieldIds.forEach((fieldId) => {
          delete draft[fieldId];
        });
        return draft;
      }
      case CONTRACT_SETUP_BY_DRAFT_SUCCESS: {
        const { participants } = action.payload;

        participants.forEach((participant) => {
          const { fields } = participant;
          fields.forEach((field) => {
            draft[field.id] = {
              page: field.position.page,
              relativeX: field.position.x,
              relativeY: field.position.y,
            };
            if (field.type === FieldType.CHECKBOX_GROUP) {
              field.children.forEach((child) => {
                draft[child.id] = {
                  page: child.position.page,
                  relativeX: child.position.x,
                  relativeY: child.position.y,
                };
              });
            }
          });
        });
        return draft;
      }
      case CONTRACT_SETUP_FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE: {
        const { fieldIds } = action.payload;
        fieldIds.forEach((key) => {
          if (key in draft) {
            delete draft[key];
          }
        });
        return draft;
      }
      default:
        return draft;
    }
  },
  initialState
);

export default reducer;
